import { Injectable } from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {map, mergeMap, share} from 'rxjs/operators';
import {Observable} from 'rxjs';
import {environment} from '@env/environment';
import {IStripePlan, ISubscription} from '@interfaces/subscription-plan';
import {ErrorService} from '@services/error/error.service';
import {IPaymentMethod} from '@interfaces/payment-method';
import {IInvoice} from '@interfaces/Invoice';
import {TranslateService} from '@ngx-translate/core';
import {ModalService} from '@services/modal/modal.service';
import {Router} from '@angular/router';
import {LocalizeRouterService} from '@gilsdav/ngx-translate-router';

@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private env = environment;

  constructor(
    private http: HttpClient,
    private errorService: ErrorService,
    private translate: TranslateService,
    private modalService: ModalService,
    private router: Router,
    private localize: LocalizeRouterService,
  ) {
  }

  public getStripeSubscriptions(): Observable<ISubscription[]> {
    const observable = this.http.get<{data: ISubscription[]}>(`${this.env.stripeApiUrl}/subscription`)
      .pipe(
        map(({data}) => data),
        share()
      );

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public getStripePlans(): Observable<IStripePlan[]> {
    const keyMap = ['day', 'week', 'month', 'year'];
    const observable = this.http
      .get<{data: IStripePlan[]}>(`${this.env.stripeApiUrl}/plan`)
      .pipe(
        share(),
        map((val: {data: IStripePlan[]}) => {
          return val.data.sort((pl1, pl2) => {
            return keyMap.indexOf(pl1.interval) - keyMap.indexOf(pl2.interval);
          });
        })
      );

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public getPaymentMethods(): Observable<{data: IPaymentMethod[]}> {
    const observable = this.http
      .get<{data: IPaymentMethod[]}>(`${this.env.stripeApiUrl}/payment-method`)
      .pipe(share());

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public getPaymentMethod(paymentMethodId: string): Observable<{data: IPaymentMethod}> {
    const observable = this.http
      .get<{data: IPaymentMethod}>(`${this.env.stripeApiUrl}/payment-method/${paymentMethodId}`)
      .pipe(share());

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public getSetupIntent(): Observable<{data: { client_secret: string }}> {
    const observable = this.http.post<{data: { client_secret: string }}>(`${this.env.stripeApiUrl}/setup-intent`, null)
      .pipe(share());

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public createSubscription(paymentMethodId: string, planId: string, devices: number): Observable<{data: ISubscription}> {
    const body = {
      payment_method: paymentMethodId,
      plan: planId,
      quantity: devices,
      coupon: ''
    };
    const observable = this.http.post<{data: ISubscription}>(`${this.env.stripeApiUrl}/subscription`, body)
      .pipe(share());

    observable.subscribe();

    return observable;
  }

  public getStripeInvoice(): Observable<{data: IInvoice[]}> {
    const observable = this.http.get<{data: IInvoice[]}>(`${this.env.stripeApiUrl}/invoice`)
      .pipe(share());

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public updateSubscription(
    subsId: string,
    data: {plan: string} | {cancel: boolean} | {resume: boolean} | {quantity: number}
  ): Observable<{data: ISubscription}> {

    const observable = this.http.patch<{data: ISubscription}>(`${this.env.stripeApiUrl}/subscription/${subsId}`, data)
      .pipe(share());

    observable.subscribe();

    return observable;
  }

  public getPaymentIntent(id: string): Observable<any> {
    const observable = this.http.get(`${this.env.stripeApiUrl}/payment-intent/${id}`)
      .pipe(share());

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public delPaymentMethod(paymentMethodId: string): Observable<any> {
    const observable = this.http.delete<Observable<any>>(`${this.env.stripeApiUrl}/payment-method/${paymentMethodId}`)
      .pipe(share());

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public addPaymentMethod(paymentMethodId: string, asDefault: boolean): Observable<{data: IPaymentMethod}> {
    const body = {
      payment_method: paymentMethodId,
      default: asDefault
    };
    const observable = this.http.post<{data: IPaymentMethod}>(`${this.env.stripeApiUrl}/payment-method`, body)
      .pipe(share());

    observable.subscribe(
      () => {},
      (err) => this.errorService.showError(err));

    return observable;
  }

  public showSuccessMsg(): void {
    this.translate
      .get('app.confirm.subscriptionSuccess')
      .pipe(
        mergeMap(msg => this.modalService.showMsg(msg))
      )
      .subscribe(() => {
        const path = this.localize.translateRoute('/app/user/subscribe');
        this.router.navigate([path]);
      });
  }

  public findSelectedStripePlan(plans: IStripePlan[], subscription: ISubscription | null): IStripePlan {
    if (subscription) return plans.find(plan => plan.id === subscription?.plan?.id) || plans[2];
    else return plans[2];
  }
}
